import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Search, Plus, X, Loader2, PenLine, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const SourceBadge = ({ src }) => (
  <span className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 text-[9px] rounded-md font-bold uppercase tracking-wider ${
    src === "tmdb" ? "bg-emerald-500/90 text-white" : "bg-amber-500/90 text-black"
  }`}>{src}</span>
);

export default function AddMovieDialog({ open, onOpenChange }) {
  const [mode, setMode] = useState("search"); // search | manual
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupIds, setGroupIds] = useState([]);
  const [tagsInput, setTagsInput] = useState("");
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  // Manual form
  const [manual, setManual] = useState({ title: "", year: "", poster: "", plot: "" });

  useEffect(() => {
    if (!open) return;
    api.get("/groups").then(r => setGroups(r.data)).catch(() => {});
    setMode("search"); setQ(""); setResults([]); setSelected(null); setGroupIds([]); setTagsInput("");
    setManual({ title: "", year: "", poster: "", plot: "" });
  }, [open]);

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const r = await api.get(`/search?q=${encodeURIComponent(q)}`);
      setResults(r.data.results || []);
    } catch { toast.error("Search failed"); }
    finally { setSearching(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const tags = tagsInput.split(",").map(t => t.trim()).filter(Boolean);
      let body;
      if (mode === "search") {
        if (!selected) { toast.error("Pick a movie or switch to manual"); setSaving(false); return; }
        body = {
          title: selected.title,
          year: selected.year,
          poster: selected.poster,
          plot: selected.overview,
          imdb_id: selected.imdb_id,
          tmdb_id: selected.tmdb_id,
          media_type: selected.media_type || "movie",
          status: "backlog",
          group_ids: groupIds,
          tags,
        };
      } else {
        if (!manual.title.trim()) { toast.error("Title is required"); setSaving(false); return; }
        body = {
          title: manual.title.trim(),
          year: manual.year || null,
          poster: manual.poster || null,
          plot: manual.plot || null,
          status: "backlog",
          group_ids: groupIds,
          tags,
        };
      }
      await api.post("/movies", body);
      toast.success(`Added "${body.title}" to backlog`);
      onOpenChange(false);
      window.dispatchEvent(new CustomEvent("movies:refresh"));
    } catch { toast.error("Failed to add movie"); }
    finally { setSaving(false); }
  };

  const toggleGroup = (id) => setGroupIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F12] border-white/10 text-slate-100 max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="add-movie-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center justify-between">
            {mode === "search" ? "Add a movie" : "Add manually"}
            <button
              onClick={() => { setMode(mode === "search" ? "manual" : "search"); setSelected(null); }}
              data-testid="toggle-mode-btn"
              className="text-xs text-slate-400 hover:text-white flex items-center gap-1 font-normal"
            >
              {mode === "search" ? <><PenLine className="w-3.5 h-3.5" /> Add manually</> : <><ArrowLeft className="w-3.5 h-3.5" /> Back to search</>}
            </button>
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {mode === "search" ? "Search TMDB + OMDB. Posters, plot, ratings auto-fetched." : "For obscure / personal entries — only the title is required."}
          </DialogDescription>
        </DialogHeader>

        {mode === "search" && (
          <>
            <div className="flex gap-2">
              <Input
                placeholder="Search any movie, anime, K-drama, Bollywood…"
                value={q}
                data-testid="omdb-search-input"
                onChange={e => setQ(e.target.value)}
                onKeyDown={e => e.key === "Enter" && search()}
                className="bg-[#1A1A1E] border-white/10 focus-visible:ring-[#E11D48] text-white"
              />
              <Button onClick={search} disabled={searching} data-testid="omdb-search-btn" className="bg-[#E11D48] hover:bg-rose-700">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            {results.length > 0 && !selected && (
              <div className="grid grid-cols-2 sm:grid-cols-3 auto-rows-max gap-3 mt-2 max-h-[60vh] overflow-y-auto pr-1" data-testid="omdb-results">
                {results.map((r, idx) => (
                  <button
                    key={`${r.source}-${r.tmdb_id || r.imdb_id || idx}`}
                    onClick={() => setSelected(r)}
                    className="group relative text-left bg-[#1A1A1E] rounded-lg overflow-hidden border border-white/5 hover:border-[#E11D48]/50 transition-all"
                    data-testid={`result-${r.tmdb_id || r.imdb_id || idx}`}
                  >
                    <SourceBadge src={r.source} />
                    <div className="relative w-full bg-black aspect-[2/3]">
                      {r.poster ? (
                        <img src={r.poster} alt={r.title} className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-slate-600 text-xs">No poster</div>
                      )}
                    </div>
                    <div className="p-2">
                      <div className="text-sm font-medium line-clamp-1">{r.title}</div>
                      <div className="text-xs text-slate-500">{r.year} {r.media_type === "tv" && "· TV"}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {q.trim() && !searching && results.length === 0 && (
              <div className="text-center py-6 text-sm text-slate-400">
                Nothing found. <button onClick={() => { setMode("manual"); setManual(m => ({ ...m, title: q })); }} className="text-[#E11D48] underline">Add it manually →</button>
              </div>
            )}

            {selected && (
              <div className="flex gap-4 p-4 bg-[#1A1A1E] rounded-lg border border-white/5">
                <div className="w-24 shrink-0 aspect-[2/3] bg-black rounded overflow-hidden">
                  {selected.poster && <img src={selected.poster} alt={selected.title} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg font-semibold">{selected.title}</div>
                  <div className="text-xs text-slate-500 mb-3">{selected.year} · via {selected.source.toUpperCase()}</div>
                  <Button variant="ghost" size="sm" onClick={() => setSelected(null)} className="text-slate-400 hover:text-white h-7 px-2" data-testid="change-selection-btn">
                    <X className="w-3 h-3 mr-1" /> Change
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {mode === "manual" && (
          <div className="space-y-3" data-testid="manual-form">
            <Input value={manual.title} onChange={e => setManual({ ...manual, title: e.target.value })} placeholder="Title (required)" data-testid="manual-title" className="bg-[#1A1A1E] border-white/10 text-white focus-visible:ring-[#E11D48]" />
            <Input value={manual.year} onChange={e => setManual({ ...manual, year: e.target.value })} placeholder="Year (optional)" data-testid="manual-year" className="bg-[#1A1A1E] border-white/10 text-white focus-visible:ring-[#E11D48]" />
            <Input value={manual.poster} onChange={e => setManual({ ...manual, poster: e.target.value })} placeholder="Poster URL (optional)" data-testid="manual-poster" className="bg-[#1A1A1E] border-white/10 text-white focus-visible:ring-[#E11D48]" />
            <Textarea value={manual.plot} onChange={e => setManual({ ...manual, plot: e.target.value })} placeholder="Plot / personal notes (optional)" rows={4} data-testid="manual-plot" className="bg-[#1A1A1E] border-white/10 text-white focus-visible:ring-[#E11D48]" />
          </div>
        )}

        {(selected || mode === "manual") && (
          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Groups</div>
              <div className="flex flex-wrap gap-2">
                {groups.length === 0 && <div className="text-sm text-slate-500">No groups yet — create some in the Groups tab.</div>}
                {groups.map(g => (
                  <button key={g.id} onClick={() => toggleGroup(g.id)} data-testid={`toggle-group-${g.id}`}
                    className={`px-3 py-1 rounded-full text-xs border transition-all ${
                      groupIds.includes(g.id) ? "bg-[#E11D48] border-[#E11D48] text-white" : "bg-transparent border-white/10 text-slate-300 hover:border-white/30"
                    }`}>{g.name}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Tags (comma separated)</div>
              <Input value={tagsInput} onChange={e => setTagsInput(e.target.value)} placeholder="anime, rewatch, date-night" data-testid="tags-input" className="bg-[#1A1A1E] border-white/10 focus-visible:ring-[#E11D48] text-white" />
            </div>
            <Button onClick={save} disabled={saving} data-testid="save-movie-btn" className="w-full bg-[#E11D48] hover:bg-rose-700 h-11">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Add to backlog
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
